import {
  USER_ROLE,
  type UserRoleValue,
} from '@/shared/constants/auth/roles.constant.ts';
import type { BoardType } from '@/shared/constants/app/routes.constant.ts';

/**
 * 게시판 기능별 권한 정책.
 *
 * 설계 원칙:
 *  - "기능 단위(create/update/delete)" 로 허용 Role 목록을 정의한다.
 *  - 게시판 타입(notices/updates/events)마다 정책이 다를 수 있으므로 type 별로 분리.
 *  - 읽기(list/detail)는 전부 public 이라 별도 정책 없음.
 *  - 본인 글 수정/삭제 같은 "소유권" 검사는 권한과 별개 축 → canModifyPost 에서 합산.
 *
 * 하드코딩 금지: 컴포넌트/가드에서 Role 을 직접 비교하지 말고
 * 반드시 이 정책과 canXxx 헬퍼를 거친다.
 */
export interface BoardPermissionPolicy {
  /** 글 작성 가능 최소 Role 목록 */
  create: readonly UserRoleValue[];
  /** 글 수정 가능 Role 목록 (소유권과 별개로, 이 Role 이면 타인 글도 수정 가능) */
  update: readonly UserRoleValue[];
  /** 글 삭제 가능 Role 목록 */
  delete: readonly UserRoleValue[];
  /**
   * 작성자 본인에게 수정/삭제를 허용할지.
   * true 면 위 update/delete Role 이 아니어도 자기 글은 수정/삭제 가능.
   * 공지/업데이트처럼 운영자만 관리하는 게시판은 false 권장.
   */
  allowAuthorSelfManage: boolean;
}

const ADMIN_ROLES = [USER_ROLE.ADMIN, USER_ROLE.SUPER_ADMIN] as const;
const MANAGER_UP = [
  USER_ROLE.MANAGER,
  USER_ROLE.ADMIN,
  USER_ROLE.SUPER_ADMIN,
] as const;

/**
 * 게시판 타입별 권한 정책 매핑.
 *
 * 현재 정책(예시 — 운영 요구에 맞게 조정):
 *  - notices(공지)   : MANAGER 이상만 작성/수정/삭제. 본인 글 자가관리 비허용(운영 일관성).
 *  - updates(업데이트): MANAGER 이상. 릴리즈 노트라 공지와 동일.
 *  - events(이벤트)   : MANAGER 이상 작성, ADMIN 이상만 삭제(노출 영향 큼).
 */
export const BOARD_PERMISSIONS: Record<BoardType, BoardPermissionPolicy> = {
  notices: {
    create: MANAGER_UP,
    update: MANAGER_UP,
    delete: ADMIN_ROLES,
    allowAuthorSelfManage: false,
  },
  updates: {
    create: MANAGER_UP,
    update: MANAGER_UP,
    delete: ADMIN_ROLES,
    allowAuthorSelfManage: false,
  },
  events: {
    create: MANAGER_UP,
    update: MANAGER_UP,
    delete: ADMIN_ROLES,
    allowAuthorSelfManage: false,
  },
  // FAQ 삭제는 일단 최고 관리자만 노출한다(서버는 ADMIN 이상 허용).
  faqs: {
    create: MANAGER_UP,
    update: MANAGER_UP,
    delete: [USER_ROLE.SUPER_ADMIN],
    allowAuthorSelfManage: false,
  },
  archives: {
    create: MANAGER_UP,
    update: MANAGER_UP,
    delete: ADMIN_ROLES,
    allowAuthorSelfManage: false,
  },
};

/**
 * 페이지당 행 개수 선택지.
 * 사용자가 UI 에서 조작 가능(요구사항: row 개수 조작 편의 제공).
 */
export const PAGE_SIZE_OPTIONS = [10, 20, 30, 50] as const;
export type PageSizeOption = (typeof PAGE_SIZE_OPTIONS)[number];

/** 기본 페이지 크기 (요구사항: 10개) */
export const DEFAULT_PAGE_SIZE: PageSizeOption = 10;

/** 기본 페이지 번호 (1-indexed) */
export const DEFAULT_PAGE = 1;

/** URL searchParams 키 — 페이지 상태를 URL 과 동기화할 때 사용 */
export const BOARD_SEARCH_PARAM = {
  PAGE: 'page',
  SIZE: 'size',
  SEARCH: 'q',
  CATEGORY: 'category',
  SUB_CATEGORY: 'subCategory',
} as const;
